import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { IPlazaPagosPagingRes, PLAZAPAGOS } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchPlazoPagos = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetPlazoPagosParams) => {
  return useQuery({
    queryKey: ['plazo-pagos', page, nombre, page_size],
    queryFn: async () => {
      return getPlazoPagos({ page, nombre, page_size });
    },
  });
};

export const useGetPlazoPago = (id: string) => {
  return useQuery({
    queryKey: ['plazo-pagos', id],
    queryFn: async () => {
      return getPlazoPago(id);
    },
  });
};

export const useCreatePlazoPagos = (navigate: NavigateFunction, returnUrl: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPlazoPagos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plazo-pagos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdatePlazoPagos = (navigate: NavigateFunction, returnUrl: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePlazoPagos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plazo-pagos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetPlazoPagosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreatePlazoPagosParams = Omit<PLAZAPAGOS, 'id_plazo_pago'>;
export type UpdatePlazoPagosParams = {
  id: string;
  data: CreatePlazoPagosParams;
};

export const getPlazoPagos = async (params?: GetPlazoPagosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<IPlazaPagosPagingRes>(
    `${urlBackend}api/v1/plazos-pago/?${queryParams}`,
    true
  );
};

export const getPlazoPago = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<PLAZAPAGOS>(`${urlBackend}api/v1/plazos-pago/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createPlazoPagos = async (data: CreatePlazoPagosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<PLAZAPAGOS>(`${urlBackend}api/v1/plazos-pago/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updatePlazoPagos = async ({ id, data }: UpdatePlazoPagosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<PLAZAPAGOS>(`${urlBackend}api/v1/plazos-pago/${id}/`, data, true);
  setAlert(true, res.message, res.status);
  return res;
};
